import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { Description, Payment } from "@mui/icons-material";
import {
  CaseWhenBenefit,
  ReimbursementRequest,
  useGetUserQuery,
} from "services/GraphQl";
import { useNotifications } from "contexts/Notification";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import moment from "moment";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { ref as storageRef } from "firebase/storage";
import { useFirebase } from "contexts/Firebase";

interface ApprovedReimbursementsTableProps {
  whenBenefit: CaseWhenBenefit;
  companyId: string;
  caseId: string;
}

// Component to fetch a single user's information
const UserInfoFetcher: React.FC<{ userId: string; children: (userInfo: any) => React.ReactNode }> = ({ 
  userId, 
  children 
}) => {
  const { data: userData } = useGetUserQuery({
    variables: { uid: userId },
    skip: !userId,
  });

  return <>{children(userData?.user)}</>;
};

const ApprovedReimbursementsTable: React.FC<ApprovedReimbursementsTableProps> = ({
  whenBenefit,
  caseId,
  companyId,
}) => {
  const reimbursementRequests = whenBenefit.reimbursementRequests || [];
  const { notifications } = useNotifications();
  const { storage } = useFirebase();
  
  // Filter for approved requests only
  const approvedRequests = reimbursementRequests.filter((request) => request.status === "APPROVED");

  // Get unique user IDs from approved requests
  const userIds = useMemo(() => 
    Array.from(new Set(
      approvedRequests
        .map(request => request.approvedBy)
        .filter(Boolean) as string[]
    )), 
    [approvedRequests]
  );

  console.log(userIds);

  const formatMonth = (monthStr: string) => {
    return moment(monthStr, "YYYY-MM").format("MMMM YYYY");
  };

  const formatMonths = (coverageMonths: string[]) => {
    if (!coverageMonths || coverageMonths.length === 0) return "—";

    if (coverageMonths.length === 1) {
      return formatMonth(coverageMonths[0]);
    }

    // For multiple months, show range or list
    const sortedMonths = [...coverageMonths].sort();
    if (coverageMonths.length <= 3) {
      return sortedMonths.map(formatMonth).join(", ");
    }

    return `${formatMonth(sortedMonths[0])} - ${formatMonth(sortedMonths[sortedMonths.length - 1])}`;
  };

  const formatDate = (date: string | Date | moment.Moment | null | undefined) => {
    if (!date) return "—";
    return moment(date).format("MMM DD, YYYY");
  };

  const getUserDisplay = (userId: string | null | undefined) => {
    if (!userId) return "—";
    
    return (
      <UserInfoFetcher userId={userId}>
        {(userInfo) => {
          if (!userInfo) return userId; // Fallback to UID if user not found
          return userInfo.name?.display || userInfo.email || userId;
        }}
      </UserInfoFetcher>
    );
  };

  const handleReimburseClick = (request: ReimbursementRequest) => {
    // TODO: Implement reimbursement functionality
    notifications.info("Reimbursement functionality coming soon");
  };

  const DownloadFile = ({ file }: { file: { name: string; type: string; path: string } }) => {
    const [downloadURL, loading, error] = useDownloadURL(storageRef(storage, file.path));
    
    if (loading) {
      return <Typography variant="body2">{file.name} (Loading...)</Typography>;
    }
    if (error) {
      return (
        <Typography variant="body2" color="error">
          Error: {file.name}: {error.message}
        </Typography>
      );
    }
    if (!downloadURL) return <Typography variant="body2">{file.name}</Typography>;
    
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Description fontSize="small" color="primary" />
        <Button
          variant="text"
          component="a"
          href={downloadURL}
          target="_blank"
          sx={{ 
            textDecoration: "none",
            fontSize: "0.875rem",
            minWidth: "auto",
            padding: 0,
            color: "primary.main",
            textTransform: "none",
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: "transparent"
            }
          }}
        >
          {file.name}
        </Button>
      </Box>
    );
  };

  const renderAttachments = (files: any[]) => {
    if (!files || files.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {files.map((file, index) => (
          <DownloadFile key={index} file={file} />
        ))}
      </Box>
    );
  };

  if (approvedRequests.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Approved Reimbursement Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No approved reimbursement requests
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
        Approved Reimbursement Requests
      </Typography>

      <TableContainer component={Paper} sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Month(s)</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Amount Requested</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Amount Approved</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Approved Date</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Approved By</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Attachments</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {approvedRequests.map((request: ReimbursementRequest) => (
              <TableRow key={request.reimbursementRequestId} sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}>
                <TableCell>
                  <Typography variant="body2">{formatMonths(request.coverageMonths)}</Typography>
                </TableCell>
                <TableCell>
                  <FormattedCurrency value={request.amount} />
                </TableCell>
                <TableCell>
                  <FormattedCurrency value={request.approvedAmount || 0} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(request.approvedAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {getUserDisplay(request.approvedBy)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {request.notes || "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  {renderAttachments(request.files)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Payment />}
                    onClick={() => handleReimburseClick(request)}
                    sx={{
                      color: "primary.main",
                      borderColor: "primary.main",
                      "&:hover": {
                        backgroundColor: "primary.light",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    Reimburse
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ApprovedReimbursementsTable; 